import React, { memo, useEffect, useRef, useState } from 'react';
import vizInfo, {
    VizT,
    VizType,
    DateVentilation,
    VentilationType,
    NatureVentilationType,
} from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hrVizInfos';
import ToggleChoice from 'routes/HumanResourcesModule/components/HrDataGrid/ToggleChoice';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { ORG_CHART_HR_NAME } from 'routes/HumanResourcesModule/business/definitions';

import { useTranslation } from 'react-i18next';
import {
    DateString,
    SimulationResultData,
    Response,
    NatureResultData,
} from 'routes/HumanResourcesModule/business/types';
import Flex from 'ui/Flex/Flex';
import { extractDates } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import KeysIndicators from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/KeysIndicators';
import { TotalResultAmounts } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import {
    useGetResultSimulationDataNatureVentilation,
    useGetResultSimulationDataTimeVentilation,
} from './helpers/hooks/resultSimulationData';
import GroupBySelector from './components/GroupBySelector';
import ResultDataGrid from './components/ResultDataGrid';
import ResultChart from './components/ResultChart';
import NatureResultDataGrid from './components/NatureResultDataGrid';
import { MenuItem, Select, styled, Typography } from '@mui/material';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import { usePrimaryColor } from 'styles/theme';
import {
    dashDateStringToMonth,
    dateToDashDateString,
} from 'routes/HumanResourcesModule/business/helpers';
import { getFirstDayOfYear } from 'helpers/helpers';

const StySelect = styled(Select, {
    name: 'StySelect',
})(({ theme }) => ({
    '.MuiSelect-select, .MuiSelect-select:focus': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.lightGrey,
        fontSize: '13px',
        padding: '0.2rem 0.8rem',
        paddingRight: 32,
        height: 'max-content',
        width: 'max-content',
        borderRadius: '22px',
        textTransform: 'unset',
    },
    '.MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-notchedOutline:focus':
        { border: 0, borderRadius: '22px' },
    'svg.MuiSelect-icon': {
        color: theme.palette.lightGrey,
    },
}));

type Props = {
    hrSimulation: HrSimulation;
};

const TimeVentilationResult = ({ hrSimulation }: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [vizType, setVizType] = useState(vizInfo.table);
    const [groupBy, setGroupBy] =
        useState<keyof SimulationResultData>(ORG_CHART_HR_NAME);

    const [temporalVentilation, setTemporalVentilation] =
        useState<DateVentilation>(DateVentilation.MONTHLY);
    const isMonthly = temporalVentilation === DateVentilation.MONTHLY;

    // To track event, we ignore the first render where we have only the default values.
    const firstRender = useRef(true);
    useEffect(() => {
        if (temporalVentilation && vizType.name && !firstRender.current) {
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.results.openPage,
                {
                    periode: t(`humanResources.${temporalVentilation}`),
                    vue: t(`humanResources.${vizType.name}`),
                },
            );
        } else {
            firstRender.current = false;
        }
    }, [temporalVentilation, vizType]);

    const isGraph = vizType.name === VizType.GRAPH;

    const {
        data: resultSimulationDataResponse,
        isLoading: isResultSimulationResponseLoading,
    } = useGetResultSimulationDataTimeVentilation(
        hrSimulation.id,
        temporalVentilation,
    );

    let totalAmountsData: TotalResultAmounts = {
        previousYearRealizedTotalAmount: 0,
        currentYearRealizedTotalAmountUntilStartDate: 0,
        currentYearRealizedTotalAmountUntilLastMonth: 0,
        currentYearSimulatedTotalAmountFromStartDate: 0,
        currentYearSimulatedTotalAmountFromLastMonth: 0,
        nextYearRealizedTotalAmount: 0,
        nextYearSimulatedTotalAmountFromLastMonth: 0,
        nextYearSimulatedTotalAmount: 0,
    };
    let dateColumns: `${number}${number}${number}`[] = [];
    const { hrSimulationData, ...totalAmounts } =
        resultSimulationDataResponse ?? {};
    // To ensure each numeric property in totalAmounts defaults to 0 if undefined

    totalAmountsData = Object.keys(totalAmountsData).reduce((acc, key) => {
        acc[key as keyof TotalResultAmounts] =
            (totalAmounts as TotalResultAmounts)[
                key as keyof TotalResultAmounts
            ] ?? 0;
        return acc;
    }, {} as TotalResultAmounts);
    const resultSimulationData = hrSimulationData?.data ?? [];
    dateColumns = extractDates<DateString>(resultSimulationData);

    const titleNode = (
        <Flex
            alignItems="center"
            sx={{
                gap: '0.5rem',
            }}
        >
            <>
                {t('humanResources.simulation.resultsBy')}
                <GroupBySelector
                    selectedValue={groupBy}
                    setSelectedValue={setGroupBy}
                />
            </>
        </Flex>
    );

    const cardHeaderActions = (
        <>
            <ToggleChoice<VizT>
                optionSelected={vizType}
                setOption={setVizType}
                option1={vizInfo.table}
                option2={vizInfo.graph}
            />
            <ToggleChoice<DateVentilation>
                optionSelected={temporalVentilation}
                setOption={setTemporalVentilation}
                option1={DateVentilation.MONTHLY}
                option2={DateVentilation.YEARLY}
            />
        </>
    );

    return (
        <>
            <KeysIndicators
                hrSimulation={hrSimulation}
                isLoading={isResultSimulationResponseLoading}
                totalAmounts={totalAmountsData}
            />
            {isGraph ? (
                <ResultChart
                    cardHeaderActions={cardHeaderActions}
                    resultSimulationData={
                        resultSimulationData as SimulationResultData[]
                    }
                    isLoading={isResultSimulationResponseLoading}
                    titleNode={titleNode}
                    groupBy={groupBy}
                    isMonthly={isMonthly}
                />
            ) : (
                <ResultDataGrid
                    hrSimulation={hrSimulation}
                    resultSimulationData={
                        resultSimulationData as SimulationResultData[]
                    }
                    isLoading={isResultSimulationResponseLoading}
                    cardHeaderActions={cardHeaderActions}
                    titleNode={titleNode}
                    dateColumns={dateColumns}
                    isMonthly={isMonthly}
                    groupBy={groupBy}
                />
            )}
        </>
    );
};

const NatureVentilationResult = ({
    hrSimulation,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const primaryColor = usePrimaryColor();
    const [ventilationType, setVentilationType] =
        useState<NatureVentilationType>(
            NatureVentilationType.SERVICE_AND_NATURE,
        );
    const {
        data: resultSimulationDataResponse,
        isLoading: isResultSimulationResponseLoading,
    } = useGetResultSimulationDataNatureVentilation(
        hrSimulation.id,
        ventilationType,
    );

    let resultSimulationData: NatureResultData[] = [];
    resultSimulationData =
        (resultSimulationDataResponse as Response<NatureResultData>)?.data ??
        ([] as NatureResultData[]);

    const titleNode = (
        <Flex
            flexDirection="column"
            sx={{
                gap: '0.5rem',
            }}
        >
            <Flex>
                <Typography sx={{ marginRight: '0.5rem' }}>
                    {t('humanResources.simulation.simulationDetail', {
                        simulationTitle: hrSimulation.title,
                    })}
                </Typography>
                <StyChip
                    label={`${dashDateStringToMonth(
                        dateToDashDateString(
                            getFirstDayOfYear(new Date(hrSimulation.startDate)),
                        ),
                    )}-${dashDateStringToMonth(hrSimulation.endDate)}`}
                    styledColor={primaryColor}
                    sx={{ marginRight: '8px' }}
                />
            </Flex>
            <Flex>
                <Typography sx={{ marginRight: '0.5rem' }}>
                    {t('humanResources.simulation.resultsBy')}
                </Typography>
                <StySelect
                    onChange={(e) =>
                        setVentilationType(
                            e.target.value as NatureVentilationType,
                        )
                    }
                    value={ventilationType}
                >
                    <MenuItem value="service_and_nature">
                        {t('humanResources.simulation.orgChartHrMapping')}
                    </MenuItem>
                    <MenuItem value="nature">
                        {t('humanResources.simulation.natures')}
                    </MenuItem>
                </StySelect>
            </Flex>
        </Flex>
    );

    return (
        <NatureResultDataGrid
            resultSimulationData={resultSimulationData as NatureResultData[]}
            isLoading={isResultSimulationResponseLoading}
            titleNode={titleNode}
            hrSimulation={hrSimulation}
            ventilationType={ventilationType}
        />
    );
};

const Result = ({ hrSimulation }: Props): React.ReactElement => {
    const [ventilationType, setVentilationType] = useState<VentilationType>(
        VentilationType.TIME,
    );

    return (
        <>
            <ToggleChoice<VentilationType>
                optionSelected={ventilationType}
                setOption={(e) => {
                    setVentilationType(e);
                    if (e === VentilationType.NATURE) {
                        trackEventPRH(
                            TRACKABLE_EVENTS.human_resources.simulation
                                .accountableDisplay,
                        );
                    }
                }}
                option1={VentilationType.TIME}
                option2={VentilationType.NATURE}
            />
            {ventilationType === VentilationType.NATURE ? (
                <NatureVentilationResult hrSimulation={hrSimulation} />
            ) : (
                <TimeVentilationResult hrSimulation={hrSimulation} />
            )}
        </>
    );
};

export default memo(Result);
